/* @jsx h */
import { computed, defineComponent, h, onMounted, ref, watch } from "vue";
import { Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import { Avatar } from "../../shared/Avatar";
import { localDate, weekdayNames } from "../../shared/dates";

type Period = { month: string; status: "open" | "closed"; submitted: number; team_size: number };
type Pref = {
  staff_id: number;
  name: string;
  color: string | null;
  contract_hours_week: number | null;
  desired_hours: number | null;
  weekdays: number[] | null;
  dates: string[] | null;
  note: string | null;
  submitted_at: string | null;
};

const monthLabel = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { month: "long", year: "numeric" });

/** This month and the next three, as YYYY-MM-01. */
const upcomingMonths = () => {
  const now = new Date();
  return [0, 1, 2, 3].map((i) => {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
  });
};

// Collect preferences before planning: open a month, watch who submitted.
export const PreferencePeriods = defineComponent({
  name: "PreferencePeriods",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const names = weekdayNames("short");
    const periods = ref<Period[]>([]);
    const selected = ref(upcomingMonths()[1]);
    const prefs = ref<Pref[]>([]);
    const loaded = ref(false);

    const loadPeriods = async () => (periods.value = (await trpc.rota.preferences.periods.query()) as Period[]);
    const loadMonth = async () => (prefs.value = (await trpc.rota.preferences.month.query({ month: selected.value })) as Pref[]);
    onMounted(async () => {
      await Promise.all([loadPeriods(), loadMonth()]);
      loaded.value = true;
    });
    watch(selected, loadMonth);

    const rows = computed(() => {
      const known = new Map(periods.value.map((p) => [p.month, p]));
      const months = [...new Set([...upcomingMonths(), ...periods.value.map((p) => p.month)])].sort();
      return months.filter((m) => m >= `${localDate().slice(0, 7)}-01` || known.has(m)).slice(-6).map((m) => ({ month: m, period: known.get(m) }));
    });

    const setStatus = async (month: string, status: "open" | "closed") => {
      try {
        await trpc.rota.preferences.setPeriod.mutate({ month, status });
        await loadPeriods();
      } catch {
        toast.showToast(t("workday.common.save_failed"), { type: "error" });
      }
    };

    return () => !loaded.value ? (
      <div class="flex justify-center rounded-2xl border border-base-300 bg-base-100 py-10">
        <Loading />
      </div>
    ) : (
      <div class="grid grid-cols-1 gap-4 lg:grid-cols-[20rem_1fr]">
        <div class="rounded-2xl border border-base-300 bg-base-100 p-4">
          <div class="mb-3 font-semibold">{t("workday.requests.preferences")}</div>
          <ul class="space-y-2">
            {rows.value.map(({ month, period }) => (
              <li key={month}>
                <button
                  class={["flex w-full items-center justify-between rounded-xl border p-3 text-left", selected.value === month ? "border-primary bg-primary/5" : "border-base-300 hover:border-base-content/30"].join(" ")}
                  onClick={() => (selected.value = month)}
                >
                  <span>
                    <span class="block font-medium">{monthLabel(month)}</span>
                    <span class="block text-xs text-base-content/60">
                      {period ? t("workday.requests.submitted_of").replace("{submitted}", String(period.submitted)).replace("{total}", String(period.team_size)) : t("workday.requests.not_started")}
                    </span>
                  </span>
                  <span class={["badge badge-sm", period?.status === "open" ? "badge-success" : "badge-ghost"].join(" ")}>
                    {period?.status === "open" ? t("workday.requests.open") : t("workday.requests.closed")}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div class="rounded-2xl border border-base-300 bg-base-100 p-4">
          <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div class="font-semibold">{monthLabel(selected.value)}</div>
            {periods.value.find((p) => p.month === selected.value)?.status === "open" ? (
              <button class="btn btn-ghost btn-sm border-base-300" onClick={() => setStatus(selected.value, "closed")}>
                <span class="material-symbols-outlined text-base">lock</span>
                {t("workday.requests.close_period")}
              </button>
            ) : (
              <button class="btn btn-primary btn-sm" onClick={() => setStatus(selected.value, "open")}>
                <span class="material-symbols-outlined text-base">lock_open</span>
                {t("workday.requests.open_period")}
              </button>
            )}
          </div>
          <p class="mb-3 text-xs text-base-content/60">{t("workday.requests.period_help")}</p>
          <ul class="divide-y divide-base-200">
            {prefs.value.map((p) => (
              <li key={p.staff_id} class="flex items-center gap-3 py-2.5">
                <Avatar name={p.name} id={p.staff_id} color={p.color} size="sm" />
                <div class="min-w-0 flex-1">
                  <div class="text-sm font-medium">{p.name}</div>
                  <div class="truncate text-xs text-base-content/60">
                    {p.submitted_at
                      ? [
                          p.weekdays?.length ? p.weekdays.map((d) => names[d - 1]).join(" ") : null,
                          p.dates?.length ? t("workday.requests.dates_count").replace("{count}", String(p.dates.length)) : null,
                          p.note,
                        ]
                          .filter(Boolean)
                          .join(" · ") || t("workday.requests.prefs_submitted")
                      : t("workday.requests.prefs_missing")}
                  </div>
                </div>
                {p.submitted_at ? (
                  <span class="text-right">
                    <span class="block text-lg font-semibold tabular-nums">{p.desired_hours != null ? `${p.desired_hours}h` : "—"}</span>
                    <span class="material-symbols-outlined text-base text-success">check_circle</span>
                  </span>
                ) : (
                  <span class="material-symbols-outlined text-base-content/30">radio_button_unchecked</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  },
});
